import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Ban } from 'lucide-react';
import { memo, useMemo } from 'react';
import { resolveSharedTiers } from '../../../lib/utils/pricingEngine';
import { CPFFClinEditor } from './CPFFClinEditor';
import { FFPClinEditor } from './FFPClinEditor';
import { GenericClinEditor } from './GenericClinEditor';
import { ParameterCell } from './ParameterCell';
import { TMClinEditor } from './TMClinEditor';
import { TravelClinEditor } from './TravelClinEditor';
// ─── Not Separately Priced ───────────────────────────────────────────────
function NSPEditor({ clinPricing }) {
    return /*#__PURE__*/ _jsxs("div", {
        className: "bg-gray-50 border border-gray-200 rounded-lg p-6 text-center",
        children: [
            /*#__PURE__*/ _jsx(Ban, {
                className: "w-8 h-8 text-gray-300 mx-auto mb-2"
            }),
            /*#__PURE__*/ _jsx("p", {
                className: "text-sm font-medium text-gray-500",
                children: "Not Separately Priced"
            }),
            /*#__PURE__*/ _jsxs("p", {
                className: "text-xs text-gray-400 mt-1",
                children: [
                    "CLIN ",
                    clinPricing.clinNumber,
                    " is NSP — it is included in the price of another CLIN and requires no separate pricing."
                ]
            })
        ]
    });
}
// ─── Non-labor pass-through ──────────────────────────────────────────────
function PassThroughEditor({ clinPricing, onChange }) {
    const amountParam = clinPricing.volumeMethod.type === 'fixed_quantity' ? clinPricing.volumeMethod.quantity : null;
    if (!amountParam) {
        return /*#__PURE__*/ _jsx("div", {
            className: "text-sm text-gray-400 py-4",
            children: "No editable fields for this CLIN type."
        });
    }
    return /*#__PURE__*/ _jsx("div", {
        className: "bg-white border border-gray-200 rounded-lg p-4",
        children: /*#__PURE__*/ _jsxs("div", {
            className: "flex items-center gap-4",
            children: [
                /*#__PURE__*/ _jsx("div", {
                    className: "text-sm font-medium text-gray-700",
                    children: "Amount"
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: "w-48",
                    children: /*#__PURE__*/ _jsx(ParameterCell, {
                        param: amountParam,
                        editable: amountParam.source !== 'government',
                        onChange: (val)=>{
                            if (clinPricing.volumeMethod.type !== 'fixed_quantity') return;
                            onChange({
                                ...clinPricing,
                                volumeMethod: {
                                    ...clinPricing.volumeMethod,
                                    quantity: {
                                        ...amountParam,
                                        value: val
                                    }
                                }
                            });
                        }
                    })
                }),
                clinPricing.volumeMethod.type === 'fixed_quantity' && /*#__PURE__*/ _jsxs("span", {
                    className: "text-xs text-gray-400",
                    children: [
                        "per ",
                        clinPricing.volumeMethod.unit
                    ]
                })
            ]
        })
    });
}
// ─── Main Editor (thin router) ──────────────────────────────────────────
export const CLINPricingEditor = /*#__PURE__*/ memo(function CLINPricingEditor({ clinPricing, rateCard, sharedTierConfigs, onChange, onSelectNode }) {
    // Resolve tiered_shared → tiered before rendering
    const resolvedClin = useMemo(()=>{
        if (clinPricing.laborStructure.type !== 'tiered_shared' || !sharedTierConfigs?.length) {
            return clinPricing;
        }
        return {
            ...clinPricing,
            laborStructure: resolveSharedTiers(clinPricing.laborStructure, sharedTierConfigs)
        };
    }, [
        clinPricing,
        sharedTierConfigs
    ]);
    // NSP CLINs — no editing needed
    if (resolvedClin.metadata.isNsp) {
        return /*#__PURE__*/ _jsx(NSPEditor, {
            clinPricing: resolvedClin
        });
    }
    // Travel CLINs with travelConfig
    if (resolvedClin.metadata.travelConfig) {
        return /*#__PURE__*/ _jsx(TravelClinEditor, {
            clinPricing: resolvedClin,
            onChange: onChange
        });
    }
    // Non-labor pass-through
    if (resolvedClin.laborStructure.type === 'none') {
        return /*#__PURE__*/ _jsx(PassThroughEditor, {
            clinPricing: resolvedClin,
            onChange: onChange
        });
    }
    const contractType = resolvedClin.metadata.contractType?.toUpperCase() ?? '';
    // Show "Edit Shared Tier" link for tiered_shared CLINs
    const isSharedTier = clinPricing.laborStructure.type === 'tiered_shared';
    // Dispatch by contract type
    if (contractType.includes('FFP') || contractType.includes('FP') || contractType === 'FIRM-FIXED-PRICE') {
        return /*#__PURE__*/ _jsxs("div", {
            className: "space-y-2",
            children: [
                isSharedTier && onSelectNode && /*#__PURE__*/ _jsx(SharedTierLinks, {
                    clinPricing: clinPricing,
                    sharedTierConfigs: sharedTierConfigs,
                    onSelectNode: onSelectNode
                }),
                /*#__PURE__*/ _jsx(FFPClinEditor, {
                    clinPricing: resolvedClin,
                    rateCard: rateCard,
                    onChange: onChange
                })
            ]
        });
    }
    if (contractType.includes('T&M') || contractType.includes('TM') || contractType.includes('LH') || contractType === 'TIME-AND-MATERIALS' || contractType === 'LABOR-HOUR') {
        return /*#__PURE__*/ _jsxs("div", {
            className: "space-y-2",
            children: [
                isSharedTier && onSelectNode && /*#__PURE__*/ _jsx(SharedTierLinks, {
                    clinPricing: clinPricing,
                    sharedTierConfigs: sharedTierConfigs,
                    onSelectNode: onSelectNode
                }),
                /*#__PURE__*/ _jsx(TMClinEditor, {
                    clinPricing: resolvedClin,
                    rateCard: rateCard,
                    onChange: onChange
                })
            ]
        });
    }
    if (contractType.includes('CPFF') || contractType.includes('CPAF') || contractType.includes('CPIF') || contractType.includes('COST-PLUS')) {
        return /*#__PURE__*/ _jsxs("div", {
            className: "space-y-2",
            children: [
                isSharedTier && onSelectNode && /*#__PURE__*/ _jsx(SharedTierLinks, {
                    clinPricing: clinPricing,
                    sharedTierConfigs: sharedTierConfigs,
                    onSelectNode: onSelectNode
                }),
                /*#__PURE__*/ _jsx(CPFFClinEditor, {
                    clinPricing: resolvedClin,
                    rateCard: rateCard,
                    onChange: onChange
                })
            ]
        });
    }
    // Default/generic
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-2",
        children: [
            isSharedTier && onSelectNode && /*#__PURE__*/ _jsx(SharedTierLinks, {
                clinPricing: clinPricing,
                sharedTierConfigs: sharedTierConfigs,
                onSelectNode: onSelectNode
            }),
            /*#__PURE__*/ _jsx(GenericClinEditor, {
                clinPricing: resolvedClin,
                rateCard: rateCard,
                onChange: onChange
            })
        ]
    });
});
// ─── Shared Tier Navigation Links ──────────────────────────────────────────
function SharedTierLinks({ clinPricing, sharedTierConfigs, onSelectNode }) {
    if (clinPricing.laborStructure.type !== 'tiered_shared' || !sharedTierConfigs?.length) {
        return null;
    }
    const configMap = new Map(sharedTierConfigs.map((c)=>[
            c.id,
            c
        ]));
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex items-center gap-2 flex-wrap text-xs",
        children: [
            /*#__PURE__*/ _jsx("span", {
                className: "text-gray-400",
                children: "Shared tiers:"
            }),
            clinPricing.laborStructure.tierRefs.map((ref)=>{
                const config = configMap.get(ref.sharedTierConfigId);
                if (!config) return null;
                return /*#__PURE__*/ _jsx("button", {
                    onClick: ()=>onSelectNode(`shared-tier-${ref.sharedTierConfigId}`),
                    className: "text-blue-600 hover:text-blue-700 hover:underline",
                    children: config.tierLabel
                }, ref.sharedTierConfigId);
            })
        ]
    });
}
